import React, { useEffect, useRef } from 'react';
import moment from 'moment';
import { useFormik } from 'formik';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { fileGroupOptions, fileStatusOptions, yesNoOptions } from '../../../utils/mics';
import { Dropdown } from 'primereact/dropdown';
import { useDispatch, useSelector } from 'react-redux';
import appActions from '../../../appActions';

const IntOutBoundFileFilterForm = ({ handleSearch, handleReset, onFormValuesChange }) => {

    const dispatch = useDispatch();
    const filters = useSelector((state) => state.intOutBoundFile.filters);

    const formik = useFormik({

        initialValues: {
            id: filters?.id || '',
            fileId: filters?.fileId || '',
            storeId: filters?.storeId || '',
            businessDate: filters?.businessDate || '',
            beginDateTime: filters?.beginDateTime || '',
            endDateTime: filters?.endDateTime || '',
            inFilePath: filters?.inFilePath || '',
            outFilePath: filters?.outFilePath || '',
            xmlStatus: filters?.xmlStatus || null,
            logText: filters?.logText || '',
            error: filters?.error || null,
            status: filters?.status || null,
            fileType: filters?.fileType || null,
            date_from: filters?.date_from || moment().subtract(30, 'days').toDate(),
            date_till: filters?.date_till || new Date(),
        },

        onSubmit: (values) => {
            const data = {
                ...values,
                businessDate: values.businessDate ? moment(values.businessDate).format('YYYY-MM-DD') : '',
                date_from: values.date_from ? moment(values.date_from).format('YYYY-MM-DD') : '',
                date_till: values.date_till ? moment(values.date_till).format('YYYY-MM-DD') : '',
            };

            dispatch(appActions.INTOUTBOUNDFILE_SET_FILTERS_REQUEST(values));

            handleSearch(data);
            // Optionally, call onFormValuesChange here as well
            if (onFormValuesChange) {
                onFormValuesChange(data);
            }
        },
       
    });

    const handleClear = () => {
        dispatch(appActions.INTOUTBOUNDFILE_SET_FILTERS_REQUEST({}));
        formik.resetForm();
        onFormValuesChange(null);
        handleReset();
    };

    const handleChange = (e) => {
        formik.handleChange(e);
        if (onFormValuesChange) {
            const values = { ...formik.values, [e.target.name]: e.target.value };
            const data = {
                ...values,
                businessDate: values.businessDate ? moment(values.businessDate).format('YYYY-MM-DD') : '',
                date_from: values.date_from ? moment(values.date_from).format('YYYY-MM-DD') : '',
                date_till: values.date_till ? moment(values.date_till).format('YYYY-MM-DD') : '',
            };
            onFormValuesChange(data);
        }
    }

    const isDateTillUpdated = useRef(false); // Track if update is needed

    useEffect(() => {
        if (
            formik.values.date_from &&
            (!formik.values.date_till || new Date(formik.values.date_till) < new Date(formik.values.date_from))
        ) {
            if (!isDateTillUpdated.current) { // Prevent infinite loop
                formik.setFieldValue("date_till", formik.values.date_from);
                isDateTillUpdated.current = true; // Mark as updated
            }
        } else {
            isDateTillUpdated.current = false; // Reset when update is not needed
        }
    }, [formik, formik.values.date_from]); // Only trigger when date_from changes

    const renderFormArea = () => {
        return (
            <>
                <div className="p-fluid formgrid grid w-full">

                    <div className="field col-12 md:col-3">
                        <label htmlFor="title">FileId</label>
                        <InputText id="fileId" name="fileId" type="text" value={formik.values.fileId} onChange={handleChange} />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label htmlFor="title">StoreId</label>
                        <InputText id="storeId" name="storeId" type="text" value={formik.values.storeId} onChange={handleChange} />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label htmlFor="businessDate">Business Date</label>
                        <Calendar id="businessDate" name="businessDate" value={formik.values.businessDate} onChange={handleChange} dateFormat="dd-mm-yy" showIcon />
                    </div>
                    
                    <div className="field col-12 md:col-3">
                        <label htmlFor="title">In File Path</label>
                        <InputText id="inFilePath" name="inFilePath" type="text" value={formik.values.inFilePath} onChange={handleChange} />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label htmlFor="title">Out File Path</label>
                        <InputText id="outFilePath" name="outFilePath" type="text" value={formik.values.outFilePath} onChange={handleChange} />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label htmlFor="title">File Type</label>
                        <Dropdown filter showClear  value={formik.values.fileType} onChange={handleChange} options={fileGroupOptions} id="fileType" name="fileType" optionLabel="name" optionValue='name' />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label htmlFor="title">Error</label>
                         <Dropdown filter showClear  value={formik.values.error} onChange={handleChange} options={yesNoOptions} id="error" name="error" optionLabel="name" optionValue='code' />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label htmlFor="title">XML Status</label>
                        <Dropdown filter showClear value={formik.values.xmlStatus} onChange={handleChange} options={yesNoOptions} id="xmlStatus" name="xmlStatus" optionLabel="name" optionValue='code' />
                    </div>
                    
                    <div className="field col-12 md:col-3">
                        <label htmlFor="status">Status</label>
                        <Dropdown filter showClear  value={formik.values.status} onChange={handleChange} options={fileStatusOptions} id="status" name="status" optionLabel="name" optionValue='code' placeholder="Select status" />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label htmlFor="title">Log Text</label>
                        <InputText id="logText" name="logText" type="text" value={formik.values.logText} onChange={handleChange} />
                    </div>

                   <div className="field col-12 md:col-3">
                        <label htmlFor="date_from">Date From</label>
                        <Calendar id="date_from" name="date_from" value={formik.values.date_from} onChange={handleChange} dateFormat="dd-mm-yy" showIcon maxDate={new Date()}/>
                    </div>
                    <div className="field col-12 md:col-3">
                        <label htmlFor="date_from">Date Till</label>
                        <Calendar id="date_till" name="date_till" value={formik.values.date_till} onChange={handleChange} dateFormat="dd-mm-yy"  showIcon minDate={formik.values.date_from}/>
                    </div>
                   
                </div>
                <div className="grid px-2 py-4 flex-row-reverse gap-10">
                    <Button type="submit" icon="pi pi-search" label="Search" severity="success" outlined className="p-button-success mr-2 tp" data-pr-tooltip="Search" />
                    <Button type="button" icon="pi pi-filter-slash" className='tp' data-pr-tooltip="Reset Filter" label="Reset Filter" severity="secondary" outlined onClick={handleClear} />
                </div>
            </>
        );
    };

    return <form onSubmit={formik.handleSubmit}>{renderFormArea()}</form>;
};

export default IntOutBoundFileFilterForm;
